import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    TbGitCommit,
    TbGitPullRequest,
    TbGitMerge,
    TbGitPullRequestClosed,
    TbGitPullRequestDraft,
    TbCircleCheck,
    TbCircleX,
    TbLoader,
    TbUnlink
} from 'react-icons/tb';
import { cn } from "@/lib/utils";
import { useTaskGitHub, useGitHubIntegration } from '@/hooks/useTaskGitHub';
import LinkGitHubDialog from './LinkGitHubDialog';

export default function TaskGitHub({ task, organizationSlug, sidebarLoading, onRefreshActivities }) {
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);

    const { status, isLoading: statusLoading } = useGitHubIntegration(organizationSlug);

    const {
        commits,
        pullRequests,
        isLoading,
        error,
        unlinkCommit,
        unlinkPR,
        refetch
    } = useTaskGitHub(task?.id, task?.key, organizationSlug);

    // Don't render anything while sidebar is loading (it has its own skeleton)
    if (sidebarLoading) {
        return null;
    }

    // Don't render anything if GitHub isn't available (not configured on server)
    if (!statusLoading && status && !status.available) {
        return null;
    }

    // Don't render if GitHub isn't installed for this org
    if (!statusLoading && status && !status.installed) {
        return null;
    }

    // Show loading state
    if (isLoading || statusLoading) {
        return (
            <div className="space-y-2">
                <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">GitHub</div>
                <div className="h-7 w-28 bg-muted animate-pulse rounded-md" />
            </div>
        );
    }

    // No linked items - show link button
    if (commits.length === 0 && pullRequests.length === 0 && !error) {
        return (
            <div className="space-y-2">
                <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">GitHub</div>
                <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setLinkDialogOpen(true)}
                >
                    Link commit or PR
                </Button>
                <LinkGitHubDialog
                    open={linkDialogOpen}
                    onOpenChange={setLinkDialogOpen}
                    taskKey={task?.key}
                    organizationSlug={organizationSlug}
                    onLinked={refetch}
                    onRefreshActivities={onRefreshActivities}
                />
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">GitHub</div>

            <div className="space-y-1">
                {/* Pull Requests */}
                {pullRequests.map(pr => (
                    <PRItem
                        key={`${pr.repositoryFullName}-${pr.number}`}
                        pr={pr}
                        onUnlink={async () => {
                            await unlinkPR(pr.number, pr.repositoryFullName);
                            onRefreshActivities?.();
                        }}
                    />
                ))}

                {/* Commits */}
                {commits.slice(0, 5).map(commit => (
                    <CommitItem
                        key={commit.sha}
                        commit={commit}
                        onUnlink={async () => {
                            await unlinkCommit(commit.sha, commit.repositoryFullName);
                            onRefreshActivities?.();
                        }}
                    />
                ))}
                {commits.length > 5 && (
                    <div className="text-xs text-muted-foreground/60 pl-6">
                        +{commits.length - 5} more commits
                    </div>
                )}

                {/* Link action */}
                <button
                    onClick={() => setLinkDialogOpen(true)}
                    className="text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                >
                    Link commit or PR
                </button>
            </div>

            <LinkGitHubDialog
                open={linkDialogOpen}
                onOpenChange={setLinkDialogOpen}
                taskKey={task?.key}
                organizationSlug={organizationSlug}
                onLinked={refetch}
                onRefreshActivities={onRefreshActivities}
            />
        </div>
    );
}

function PRItem({ pr, onUnlink }) {
    const { state, isDraft, reviewStatus, checksStatus, number, title, url, author, headBranch, baseBranch, createdAt } = pr;

    let StateIcon;
    let stateColor;
    let stateLabel;

    if (state === 'merged') {
        StateIcon = TbGitMerge;
        stateColor = 'text-purple-500';
        stateLabel = 'Merged';
    } else if (state === 'closed') {
        StateIcon = TbGitPullRequestClosed;
        stateColor = 'text-red-500';
        stateLabel = 'Closed';
    } else if (isDraft) {
        StateIcon = TbGitPullRequestDraft;
        stateColor = 'text-muted-foreground';
        stateLabel = 'Draft';
    } else {
        StateIcon = TbGitPullRequest;
        stateColor = 'text-green-500';
        stateLabel = 'Open';
    }

    // Check status indicator
    let CheckIcon = null;
    let checkColor = '';

    if (state === 'open' && !isDraft) {
        switch (checksStatus) {
            case 'success':
                CheckIcon = TbCircleCheck;
                checkColor = 'text-green-500';
                break;
            case 'failure':
                CheckIcon = TbCircleX;
                checkColor = 'text-red-500';
                break;
        }
    }

    // Truncate title for main view
    const truncatedTitle = title.length > 30 ? title.substring(0, 27) + '...' : title;

    return (
        <TooltipProvider delayDuration={200}>
            <div className="group flex items-center gap-2 py-1 px-2 -mx-2 rounded-md hover:bg-muted/50">
                <StateIcon className={cn("w-3.5 h-3.5 flex-shrink-0", stateColor)} />
                <div className="flex-grow min-w-0 flex items-center gap-1.5">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-muted-foreground hover:text-foreground truncate"
                            >
                                <span className="text-foreground">#{number}</span>
                                <span className="ml-1.5">{truncatedTitle}</span>
                            </a>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" sideOffset={4} className="px-3 py-2.5 max-w-[300px]">
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium leading-snug hover:underline block"
                            >
                                {title}
                            </a>
                            <p className="text-[11px] text-muted-foreground mt-2 font-mono truncate">
                                <a
                                    href={`https://github.com/${pr.repositoryFullName}/tree/${headBranch}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline hover:text-foreground"
                                >
                                    {headBranch}
                                </a>
                                <span> → </span>
                                <a
                                    href={`https://github.com/${pr.repositoryFullName}/tree/${baseBranch}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline hover:text-foreground"
                                >
                                    {baseBranch}
                                </a>
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                <a
                                    href={`https://github.com/${author?.login}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline hover:text-foreground"
                                >
                                    {author?.login}
                                </a>
                                <span> · {stateLabel}</span>
                            </p>
                        </TooltipContent>
                    </Tooltip>
                    {CheckIcon && <CheckIcon className={cn("w-3 h-3 flex-shrink-0", checkColor)} />}
                    {reviewStatus === 'approved' && (
                        <span className="text-[10px] text-green-600 dark:text-green-400 flex-shrink-0">Approved</span>
                    )}
                    {reviewStatus === 'changes_requested' && (
                        <span className="text-[10px] text-orange-600 dark:text-orange-400 flex-shrink-0">Changes</span>
                    )}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={onUnlink}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            >
                                <TbUnlink className="w-3 h-3" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Unlink</TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </TooltipProvider>
    );
}

function CommitItem({ commit, onUnlink }) {
    const { sha, shortSha, message, author, timestamp, url, branchName } = commit;

    // Get first line of commit message
    const firstLine = message.split('\n')[0];
    const truncatedMessage = firstLine.length > 35
        ? firstLine.substring(0, 32) + '...'
        : firstLine;

    // Format timestamp
    const commitDate = new Date(timestamp);
    const formattedDate = commitDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: commitDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });

    return (
        <TooltipProvider delayDuration={200}>
            <div className="group flex items-center gap-2 py-1 px-2 -mx-2 rounded-md hover:bg-muted/50">
                <TbGitCommit className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-grow min-w-0 flex items-center gap-1.5">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-muted-foreground hover:text-foreground truncate"
                            >
                                <span className="font-mono text-foreground">{shortSha}</span>
                                <span className="ml-1.5">{truncatedMessage}</span>
                            </a>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" sideOffset={4} className="px-3 py-2.5 max-w-[300px]">
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs leading-snug hover:underline block"
                            >
                                {firstLine}
                            </a>
                            <p className="text-[11px] text-muted-foreground mt-2">
                                {author?.username ? (
                                    <a
                                        href={`https://github.com/${author.username}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline hover:text-foreground"
                                    >
                                        {author.username}
                                    </a>
                                ) : (
                                    <span>{author?.name}</span>
                                )}
                                <span> · {formattedDate}</span>
                                {branchName && (
                                    <>
                                        <span> · </span>
                                        <a
                                            href={`https://github.com/${commit.repositoryFullName}/tree/${branchName}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono hover:underline hover:text-foreground"
                                        >
                                            {branchName}
                                        </a>
                                    </>
                                )}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={onUnlink}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            >
                                <TbUnlink className="w-3 h-3" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>Unlink</TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </TooltipProvider>
    );
}
